export type AdminSubscriptionKind = 'creator' | 'campaign_host';

export type AdminCreatorSubStatus =
    | 'active'
    | 'trialing'
    | 'canceled'
    | 'cancelled'
    | 'non-renewing'
    | 'past_due'
    | 'attention'
    | 'expired'
    | string;

export type AdminCreatorSubSource = 'stripe' | 'paystack' | 'admin_grant' | 'trial' | string;

export interface IAdminCreatorSubscriptionRow {
    id: string;
    kind?: AdminSubscriptionKind;
    status: AdminCreatorSubStatus | null;
    active: boolean;
    amount: number;
    currency?: string | null;
    paid: boolean;
    plan_class: string | null;
    plan_name: string | null;
    plan_code: string | null;
    interval: string | null;
    payment_source: string;
    is_admin_grant: boolean;
    is_trial: boolean;
    trial_days: number | null;
    trial_end: string | null;
    start_date: string | null;
    current_period_end: string | null;
    next_payment_date: string | null;
    stripe_subscription_id?: string | null;
    paystack_subscription_code?: string | null;
    stripe_region?: string | null;
    has_paystack_authorization?: boolean;
    first_charge_reference?: string | null;
    created_at: string | null;
    user: {
        id: string;
        email?: string | null;
        userName?: string | null;
        firstName?: string | null;
        lastName?: string | null;
        role?: string | null;
        creator_trial?: { used?: boolean; used_at?: string } | null;
    };
}

export interface IAdminCreatorSubscriptionListQuery {
    page?: number;
    limit?: number;
    status?: string;
    planClass?: string;
    source?: string;
    search?: string;
    isTrial?: boolean;
    isAdminGrant?: boolean;
    userId?: string;
    /** When true, include $0 / system free tiers. Default false on API. */
    includeFree?: boolean;
    /** When false, include canceled/expired. Default true (in-use only) when status unset. */
    inUse?: boolean;
}

export interface IAdminCreatorSubscriptionListResult {
    rows: IAdminCreatorSubscriptionRow[];
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    results: number;
    summary: {
        total_amount: number;
        count: number;
        trialing: number;
        active: number;
    };
    filters: {
        status: string[];
        planClass: string[];
        source: string[];
    };
}

export function buildAdminCreatorSubscriptionListQuery(params: IAdminCreatorSubscriptionListQuery): string {
    const q = new URLSearchParams();
    if (params.page) q.set('page', String(params.page));
    if (params.limit) q.set('limit', String(params.limit));
    if (params.status) q.set('status', params.status);
    if (params.planClass) q.set('planClass', params.planClass);
    if (params.source) q.set('source', params.source);
    if (params.search?.trim()) q.set('search', params.search.trim());
    if (params.isTrial) q.set('isTrial', 'true');
    if (params.isAdminGrant) q.set('isAdminGrant', 'true');
    if (params.userId) q.set('userId', params.userId);
    if (params.includeFree) q.set('includeFree', 'true');
    if (params.inUse === false) q.set('inUse', 'false');
    const s = q.toString();
    return s ? `?${s}` : '';
}

export function parseAdminCreatorSubscriptionListBody(body: any): IAdminCreatorSubscriptionListResult {
    const rowsRaw = body?.data?.data ?? body?.data ?? [];
    return {
        rows: Array.isArray(rowsRaw) ? rowsRaw : [],
        page: Number(body?.page ?? body?.currentPage ?? 1),
        limit: Number(body?.limit ?? body?.pageSize ?? 25),
        totalCount: Number(body?.totalCount ?? 0),
        totalPages: Number(body?.totalPages ?? 1),
        results: Number(body?.results ?? 0),
        summary: body?.summary ?? { total_amount: 0, count: 0, trialing: 0, active: 0 },
        filters: body?.filters ?? {
            status: ['active', 'trialing', 'canceled'],
            planClass: ['basic', 'starter', 'pro', 'pro plus'],
            source: ['stripe', 'paystack', 'admin_grant', 'trial'],
        },
    };
}

/** @deprecated Prefer actions from redux/adminSubscriptions/actions via useDispatchPromise. */
export {
    fetchAdminSubscriptionsByKind,
    expireAdminCreatorTrial,
    cancelAdminCreatorSubscription,
    cancelAdminCampaignHostSubscription,
    resetAdminCreatorTrial,
    fetchAdminPaystackSwitchPlans,
    switchAdminCreatorPaystackPlan,
    chargeAdminCreatorPaystackAuthorization,
} from '../redux/adminSubscriptions/actions';

export interface ISwitchAdminCreatorPaystackPlanPayload {
    planId?: string;
    plan_code?: string;
    planClass?: string;
    authorization_code: string;
}

export function isAdminCreatorPaystackRow(row: IAdminCreatorSubscriptionRow): boolean {
    return String(row.payment_source || '')
        .toLowerCase()
        .includes('paystack');
}

/** Paystack creator row with reusable AUTH that can be charged from admin. */
export function canAdminChargeCreatorPaystackAuth(row: IAdminCreatorSubscriptionRow): boolean {
    if (row.kind === 'campaign_host') return false;
    if (!isAdminCreatorPaystackRow(row)) return false;
    if (row.is_trial || String(row.status || '').toLowerCase() === 'trialing') return false;
    if (!(Number(row.amount) > 0)) return false;
    const status = String(row.status || '').toLowerCase();
    if (['canceled', 'cancelled', 'expired', 'refunded', 'complete'].includes(status)) return false;
    return Boolean(row.has_paystack_authorization);
}

export function formatAdminSubAmount(amount: number, currency?: string | null): string {
    const major = (Number(amount) || 0) / 100;
    const cur = (currency || 'USD').toUpperCase();
    if (cur === 'NGN') return `₦${major.toLocaleString('en-NG', { maximumFractionDigits: 0 })}`;
    return `$${major.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatAdminSubUser(row: IAdminCreatorSubscriptionRow): string {
    const u = row.user;
    if (!u) return 'Unknown user';
    const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
    return name || u.userName || u.email || String(u.id);
}
